import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { ReactNode } from 'react'

export interface ErrorComponentProps {
	error: unknown
	children?: ReactNode
}

function tryJsonStringify(value: unknown): string | null {
	try {
		return JSON.stringify(value, null, 2)
	} catch {
		return null
	}
}

function extractMessageFromObject(
	errorObject: Record<string, unknown>,
): string | null {
	const message = errorObject.message

	if (typeof message === 'string') {
		return message
	}

	if (message && typeof message === 'object') {
		return tryJsonStringify(message)
	}

	return null
}

function extractIssueMessages(
	errorObject: Record<string, unknown>,
): string | null {
	if (!Array.isArray(errorObject.issues)) {
		return null
	}

	const issueMessages = errorObject.issues
		.map((issue) => {
			if (!issue || typeof issue !== 'object') {
				return null
			}

			const issueMessage = (issue as Record<string, unknown>).message
			return typeof issueMessage === 'string' ? issueMessage : null
		})
		.filter((issue): issue is string => Boolean(issue))

	if (issueMessages.length === 0) {
		return null
	}

	return issueMessages.join('\n')
}

function toErrorMessage(error: unknown): string {
	if (typeof error === 'string') {
		return error
	}

	if (error instanceof Error) {
		return error.message || 'Unknown error'
	}

	if (error && typeof error === 'object') {
		const errorObject = error as Record<string, unknown>

		if (Object.keys(errorObject).length === 0) {
			return 'Unknown error'
		}

		return (
			extractMessageFromObject(errorObject) ??
			extractIssueMessages(errorObject) ??
			tryJsonStringify(errorObject) ??
			'Unknown error'
		)
	}

	return 'Unknown error'
}

export function ErrorComponent({ error }: Readonly<ErrorComponentProps>) {
	const errorMessage = toErrorMessage(error)

	if (import.meta.env.DEV) {
		// biome-ignore lint/suspicious/noConsole: We want to log the error for debugging purposes.
		console.error('Error occurred', error)
	}
	// Side effect: log error only once per error
	else if (errorMessage !== 'Unknown error')
		// biome-ignore lint/suspicious/noConsole: We want to log the error for debugging purposes.
		console.error('Error occurred', error)
	return (
		<Box sx={{ p: 2, bgcolor: 'error.main', color: 'white' }}>
			<Typography variant="h6">Error</Typography>
			<details style={{ whiteSpace: 'pre-wrap' }}>
				<Typography component="summary" variant="body2">
					Something went wrong.
				</Typography>
				<pre>{errorMessage}</pre>
			</details>
		</Box>
	)
}
